import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';
import { getVisionOrientationByPage } from './Viewer/TagHighlightUtils';

export const getAlphabeticDocumentLabel = (index) => {
  let value = Number(index) + 1;
  if (!Number.isInteger(value) || value < 1) return 'A';

  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
};

const ensureUniqueDocumentNames = (documents) => {
  const usedNames = new Set();
  return documents.map((document) => {
    const baseName = String(document.type || 'document').trim() || 'document';
    let uniqueName = baseName;
    if (usedNames.has(uniqueName.toLowerCase())) {
      if (/^[A-Z]+$/.test(baseName)) {
        let labelIndex = usedNames.size;
        do {
          uniqueName = getAlphabeticDocumentLabel(labelIndex);
          labelIndex += 1;
        } while (usedNames.has(uniqueName.toLowerCase()));
      } else {
        let suffix = 2;
        while (usedNames.has(uniqueName.toLowerCase())) {
          uniqueName = `${baseName}_${suffix}`;
          suffix += 1;
        }
      }
    }
    usedNames.add(uniqueName.toLowerCase());
    return { ...document, type: uniqueName };
  });
};

const getDocumentColorSeed = (name, documentIndex) => (
  `${name}::split-document-${documentIndex + 1}`
);

const PAGE_VIEW_CONFIG = {
  compact: {
    containerColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
    cardColumns: '40px minmax(0, 1fr)',
    thumbnailWidth: 40,
    thumbnailHeight: 54,
    documentFontSize: '0.72rem',
    documentChipHeight: 21,
  },
  list: {
    containerColumns: '1fr',
    cardColumns: '56px minmax(0, 1fr)',
    thumbnailWidth: 56,
    thumbnailHeight: 76,
    documentFontSize: '0.8rem',
    documentChipHeight: 23,
  },
  thumbnails: {
    containerColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    cardColumns: '84px minmax(0, 1fr)',
    thumbnailWidth: 84,
    thumbnailHeight: 112,
    documentFontSize: '0.82rem',
    documentChipHeight: 24,
  },
};

const INITIAL_THUMBNAIL_PAGE_LIMIT = 20;

export const getInitialThumbnailPages = (pageCount) => new Set(
  Array.from(
    { length: Math.min(Math.max(Number(pageCount) || 0, 0), INITIAL_THUMBNAIL_PAGE_LIMIT) },
    (_, index) => index + 1,
  ),
);

const normalizeDocuments = (review) => {
  const rawDocuments = Array.isArray(review?.documents) ? review.documents : [];
  const pageCount = Math.max(Number(review?.page_count) || 0, 0);
  const sortedDocuments = rawDocuments
    .map((document, index) => ({ document, originalIndex: index, startPage: Number(document?.pagenbs) }))
    .sort((left, right) => left.startPage - right.startPage || left.originalIndex - right.originalIndex);

  const documents = sortedDocuments.map(({ document }, documentIndex) => {
    const explicitPages = Array.isArray(document?.pages)
      ? document.pages
        .map((page) => Number(page))
        .filter((page) => Number.isInteger(page) && page >= 1 && (!pageCount || page <= pageCount))
      : null;
    const startPage = Number(document?.pagenbs);
    const nextStartPage = Number(sortedDocuments[documentIndex + 1]?.document?.pagenbs);
    const rangeStart = Number.isInteger(startPage) && startPage >= 1 ? startPage : 1;
    const rangeEnd = Number.isInteger(nextStartPage) && nextStartPage > rangeStart
      ? nextStartPage - 1
      : pageCount || rangeStart;
    const pages = Array.from(new Set(explicitPages || Array.from(
      { length: Math.max(rangeEnd - rangeStart + 1, 1) },
      (_, pageIndex) => rangeStart + pageIndex,
    ))).sort((left, right) => left - right);

    const fallbackName = getAlphabeticDocumentLabel(documentIndex);
    const documentName = String(document?.name || document?.type || fallbackName).trim() || fallbackName;
    return {
      pagenbs: String(pages[0] || rangeStart),
      pages,
      type: documentName,
    };
  }).filter((document) => document.pages.length > 0);

  if (documents.length === 0) {
    return [{ pagenbs: '1', pages: pageCount ? [1] : [], type: 'A' }];
  }
  return ensureUniqueDocumentNames(documents);
};

const getPageCount = (review, thumbnailPageCount) => Math.max(
  Number(review?.page_count) || 0,
  Number(thumbnailPageCount) || 0,
);

const getDocumentIndexForPage = (documents, pageNumber) => {
  const matchingIndex = documents.findIndex((document) => document.pages.includes(pageNumber));
  return matchingIndex >= 0 ? matchingIndex : 0;
};

const formatPageRanges = (pages) => {
  const sortedPages = Array.from(new Set((Array.isArray(pages) ? pages : [])
    .map((page) => Number(page))
    .filter((page) => Number.isInteger(page) && page >= 1)))
    .sort((left, right) => left - right);
  if (!sortedPages.length) return '';

  const ranges = [];
  let rangeStart = sortedPages[0];
  let rangeEnd = rangeStart;
  sortedPages.slice(1).forEach((page) => {
    if (page === rangeEnd + 1) {
      rangeEnd = page;
      return;
    }
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`);
    rangeStart = page;
    rangeEnd = page;
  });
  ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`);
  return ranges.join(', ');
};

const SplitReviewEditor = ({
  review,
  timeevent = null,
  pdfUrl,
  focusPage = null,
  focusPageRequest = 0,
  onDocumentFilterChange = null,
  disabled = false,
  saving = false,
  onChange,
  onPageSelect,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const reviewKey = JSON.stringify({
    pageCount: review?.page_count || 0,
    documents: review?.documents || [],
  });
  const initialDocuments = useMemo(() => normalizeDocuments(JSON.parse(reviewKey)), [reviewKey]);
  const [documents, setDocuments] = useState(initialDocuments);
  const [thumbnailPages, setThumbnailPages] = useState([]);
  const [thumbnailPageCount, setThumbnailPageCount] = useState(0);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState('');
  const [thumbnailPdfVersion, setThumbnailPdfVersion] = useState(0);
  const thumbnailContainerRef = useRef(null);
  const thumbnailCardRefs = useRef(new Map());
  const thumbnailPdfRef = useRef(null);
  const renderedThumbnailPagesRef = useRef(new Set());
  const renderingThumbnailPagesRef = useRef(new Set());
  const visibleThumbnailPagesRef = useRef(new Set());
  const [visibleThumbnailPages, setVisibleThumbnailPages] = useState(new Set());
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [draggedPages, setDraggedPages] = useState([]);
  const [dragPreviewTargetPage, setDragPreviewTargetPage] = useState(null);
  const hoverPageTimerRef = useRef(null);
  const [hoveredPage, setHoveredPage] = useState(null);
  const [hoverNavigationPage, setHoverNavigationPage] = useState(null);
  const [activeDocumentPage, setActiveDocumentPage] = useState(null);
  const [pageView, setPageView] = useState('compact');
  const visionOrientationByPage = useMemo(
    () => getVisionOrientationByPage(timeevent),
    [timeevent],
  );
  const pageCount = getPageCount(review, thumbnailPageCount);
  const activeDocumentIndex = useMemo(() => {
    if (activeDocumentPage == null) return -1;
    return documents.findIndex((document) => Number(document.pagenbs) === activeDocumentPage);
  }, [activeDocumentPage, documents]);
  const visiblePageNumbers = useMemo(() => {
    if (activeDocumentIndex >= 0) return documents[activeDocumentIndex].pages;
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }, [activeDocumentIndex, documents, pageCount]);

  const cancelPageHover = useCallback(() => {
    if (hoverPageTimerRef.current) {
      clearTimeout(hoverPageTimerRef.current);
      hoverPageTimerRef.current = null;
    }
  }, []);

  const schedulePageHover = useCallback((pageNumber) => {
    if (disabled || !onPageSelect) return;
    cancelPageHover();
    setHoverNavigationPage(pageNumber);
    hoverPageTimerRef.current = setTimeout(() => {
      onPageSelect(pageNumber);
      hoverPageTimerRef.current = null;
    }, 500);
  }, [cancelPageHover, disabled, onPageSelect]);

  useEffect(() => cancelPageHover, [cancelPageHover]);

  useEffect(() => {
    if (hoverNavigationPage != null && Number(focusPage) === hoverNavigationPage) {
      setHoverNavigationPage(null);
    }
  }, [focusPage, focusPageRequest, hoverNavigationPage]);

  useEffect(() => {
    setDocuments(initialDocuments);
    setSelectedPages([]);
    setSelectionAnchor(null);
    setActiveDocumentPage(null);
    onDocumentFilterChange?.(null);
  }, [initialDocuments, onDocumentFilterChange]);

  const renderThumbnail = useCallback(async (pageNumber) => {
    const pdf = thumbnailPdfRef.current;
    if (
      !pdf
      || renderedThumbnailPagesRef.current.has(pageNumber)
      || renderingThumbnailPagesRef.current.has(pageNumber)
    ) {
      return;
    }

    renderingThumbnailPagesRef.current.add(pageNumber);
    try {
      const page = await pdf.getPage(pageNumber);
      try {
        const visionOrientation = visionOrientationByPage[pageNumber] || { rotation: 0, fineAngle: 0 };
        const pageRotation = ((Number(page.rotate) || 0) + visionOrientation.rotation + 360) % 360;
        const viewport = page.getViewport({ scale: 0.18, rotation: pageRotation });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Unable to render page thumbnail');

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        if (!visibleThumbnailPagesRef.current.has(pageNumber)) return;
        renderedThumbnailPagesRef.current.add(pageNumber);
        setThumbnailPages((previousPages) => (
          previousPages.some((thumbnail) => thumbnail.pageNumber === pageNumber)
            ? previousPages
            : [...previousPages, { pageNumber, dataUrl }]
        ));
      } finally {
        page.cleanup();
      }
    } catch (error) {
      console.error(`Error rendering split review thumbnail for page ${pageNumber}:`, error);
    } finally {
      renderingThumbnailPagesRef.current.delete(pageNumber);
    }
  }, [visionOrientationByPage]);

  useEffect(() => {
    if (!pdfUrl) {
      thumbnailPdfRef.current = null;
      setThumbnailPages([]);
      setThumbnailPageCount(0);
      setVisibleThumbnailPages(new Set());
      setThumbnailLoading(false);
      setThumbnailError('');
      return undefined;
    }

    let cancelled = false;
    let loadingTask = null;
    let loadedPdf = null;
    const previousPdf = thumbnailPdfRef.current;
    thumbnailPdfRef.current = null;
    if (previousPdf) previousPdf.destroy();
    renderedThumbnailPagesRef.current.clear();
    renderingThumbnailPagesRef.current.clear();
    setThumbnailPages([]);
    setThumbnailPageCount(0);
    setVisibleThumbnailPages(new Set());
    setThumbnailLoading(true);
    setThumbnailError('');

    const loadPdf = async () => {
      try {
        const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist/build/pdf.mjs');
        if (cancelled) return;

        GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
        loadingTask = getDocument({ url: pdfUrl, isEvalSupported: false });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }

        loadedPdf = pdf;
        thumbnailPdfRef.current = pdf;
        setThumbnailPageCount(pdf.numPages);
        setThumbnailPdfVersion((previousVersion) => previousVersion + 1);
        setThumbnailLoading(false);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading split review PDF thumbnails:', error);
          setThumbnailError(t('validation_workspace.split_review_thumbnail_error'));
          setThumbnailLoading(false);
        }
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
      thumbnailPdfRef.current = null;
      if (loadingTask) loadingTask.destroy();
      if (loadedPdf) loadedPdf.destroy();
    };
  }, [pdfUrl, t, visionOrientationByPage]);

  useEffect(() => {
    visibleThumbnailPagesRef.current = visibleThumbnailPages;
    renderedThumbnailPagesRef.current.forEach((pageNumber) => {
      if (!visibleThumbnailPages.has(pageNumber)) {
        renderedThumbnailPagesRef.current.delete(pageNumber);
      }
    });
    setThumbnailPages((previousPages) => {
      const nextPages = previousPages.filter((thumbnail) => visibleThumbnailPages.has(thumbnail.pageNumber));
      return nextPages.length === previousPages.length ? previousPages : nextPages;
    });
  }, [visibleThumbnailPages]);

  useEffect(() => {
    visibleThumbnailPages.forEach((pageNumber) => {
      renderThumbnail(pageNumber);
    });
  }, [renderThumbnail, thumbnailPdfVersion, visibleThumbnailPages]);

  const setThumbnailCardRef = useCallback((pageNumber, node) => {
    if (!node) {
      thumbnailCardRefs.current.delete(pageNumber);
      return;
    }
    if (Number.isInteger(pageNumber) && pageNumber > 0) {
      thumbnailCardRefs.current.set(pageNumber, node);
    }
  }, []);

  useEffect(() => {
    const root = thumbnailContainerRef.current;
    if (!root || !pageCount) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleThumbnailPages(new Set(visiblePageNumbers.slice(0, INITIAL_THUMBNAIL_PAGE_LIMIT)));
      return undefined;
    }

    // Some browsers do not emit the initial observer entries until the scroll
    // container is laid out again (for example after changing the view mode).
    // Seed the first viewport so thumbnails begin rendering immediately.
    setVisibleThumbnailPages((previousPages) => (
      previousPages.size > 0
        ? previousPages
        : new Set(visiblePageNumbers.slice(0, INITIAL_THUMBNAIL_PAGE_LIMIT))
    ));

    const observer = new IntersectionObserver((entries) => {
      setVisibleThumbnailPages((previousPages) => {
        const nextPages = new Set(previousPages);
        let changed = false;
        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.dataset.pageNumber);
          if (!Number.isInteger(pageNumber)) return;
          if (entry.isIntersecting && !nextPages.has(pageNumber)) {
            nextPages.add(pageNumber);
            changed = true;
          } else if (!entry.isIntersecting && nextPages.delete(pageNumber)) {
            changed = true;
          }
        });
        return changed ? nextPages : previousPages;
      });
    }, { root, rootMargin: '600px 0px' });

    thumbnailCardRefs.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [pageCount, pageView, pdfUrl, visiblePageNumbers]);

  const pageViewConfig = PAGE_VIEW_CONFIG[pageView] || PAGE_VIEW_CONFIG.compact;
  const thumbnailByPage = useMemo(() => new Map(
    thumbnailPages.map((thumbnail) => [thumbnail.pageNumber, thumbnail.dataUrl])
  ), [thumbnailPages]);
  const selectedPageSet = useMemo(() => new Set(selectedPages), [selectedPages]);
  const draggedPageSet = useMemo(() => new Set(draggedPages), [draggedPages]);

  const updateDocuments = useCallback((nextDocuments) => {
    const normalizedDocuments = ensureUniqueDocumentNames(nextDocuments
      .map((document) => {
        const pages = Array.from(new Set((Array.isArray(document.pages) ? document.pages : [])
          .map((page) => Number(page))
          .filter((page) => Number.isInteger(page) && page >= 1 && page <= pageCount)))
          .sort((left, right) => left - right);
        return {
          pagenbs: String(pages[0] || Number(document.pagenbs) || 1),
          pages,
          type: String(document.type || 'document').trim() || 'document',
        };
      })
      .filter((document) => document.pages.length > 0)
      .sort((left, right) => Number(left.pagenbs) - Number(right.pagenbs)));
    setDocuments(normalizedDocuments);
    onChange?.(normalizedDocuments);
  }, [onChange, pageCount]);

  const togglePageSelection = useCallback((pageNumber, event = {}) => {
    if (disabled) return;
    cancelPageHover();
    setHoverNavigationPage(null);
    onPageSelect?.(pageNumber);
    const isRangeSelection = event.shiftKey && selectionAnchor != null;
    const isMultiSelection = event.ctrlKey || event.metaKey;

    if (isRangeSelection) {
      const start = Math.min(selectionAnchor, pageNumber);
      const end = Math.max(selectionAnchor, pageNumber);
      const range = Array.from({ length: end - start + 1 }, (_, index) => start + index);
      setSelectedPages((previousPages) => (
        isMultiSelection
          ? Array.from(new Set([...previousPages, ...range])).sort((left, right) => left - right)
          : range
      ));
    } else if (isMultiSelection) {
      setSelectedPages((previousPages) => (
        previousPages.includes(pageNumber)
          ? previousPages.filter((page) => page !== pageNumber)
          : [...previousPages, pageNumber].sort((left, right) => left - right)
      ));
      setSelectionAnchor(pageNumber);
    } else {
      setSelectedPages((previousPages) => (
        previousPages.includes(pageNumber)
          ? previousPages.filter((page) => page !== pageNumber)
          : [...previousPages, pageNumber].sort((left, right) => left - right)
      ));
      setSelectionAnchor(pageNumber);
    }
  }, [cancelPageHover, disabled, onPageSelect, selectionAnchor]);

  const createDocumentFromSelectedPages = useCallback((pages) => {
    if (disabled || !pages.length) return;
    const pagesToGroup = Array.from(new Set(pages
      .map((page) => Number(page))
      .filter((page) => Number.isInteger(page) && page >= 1 && page <= pageCount)))
      .sort((left, right) => left - right);
    if (!pagesToGroup.length) return;

    const selectedPageSetForGrouping = new Set(pagesToGroup);
    const sourceDocument = documents[getDocumentIndexForPage(documents, pagesToGroup[0])];
    const remainingDocuments = documents
      .map((document) => ({
        ...document,
        pages: document.pages.filter((page) => !selectedPageSetForGrouping.has(page)),
      }))
      .filter((document) => document.pages.length > 0);
    updateDocuments([
      ...remainingDocuments,
      {
        pagenbs: String(pagesToGroup[0]),
        pages: pagesToGroup,
        type: sourceDocument?.type || 'document',
      },
    ]);
    setSelectedPages([]);
    setSelectionAnchor(null);
  }, [disabled, documents, pageCount, updateDocuments]);

  const mergePagesIntoOneDocument = useCallback((pages, targetPage = null) => {
    if (disabled || !pages.length) return;
    const validPages = pages
      .map((page) => Number(page))
      .filter((page) => Number.isInteger(page) && page >= 1 && page <= pageCount);
    if (!validPages.length) return;

    const targetPageNumber = Number(targetPage);
    const pagesToGroup = Array.from(new Set(
      Number.isInteger(targetPageNumber) && targetPageNumber >= 1 && targetPageNumber <= pageCount
        ? [...validPages, targetPageNumber]
        : validPages,
    )).sort((left, right) => left - right);
    const targetDocumentIndex = getDocumentIndexForPage(
      documents,
      targetPageNumber || pagesToGroup[0],
    );
    const pagesToGroupSet = new Set(pagesToGroup);
    const nextDocuments = documents
      .map((document, documentIndex) => ({
        ...document,
        pages: Array.from(new Set([
          ...document.pages.filter((page) => !pagesToGroupSet.has(page)),
          ...(documentIndex === targetDocumentIndex ? pagesToGroup : []),
        ])).sort((left, right) => left - right),
      }))
      .filter((document) => document.pages.length > 0)
      .map((document) => ({
        ...document,
        pagenbs: String(document.pages[0]),
      }));

    updateDocuments(nextDocuments);
    setSelectedPages([]);
    setSelectionAnchor(null);
  }, [disabled, documents, pageCount, updateDocuments]);

  const handlePageDragStart = useCallback((event, pageNumber) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    cancelPageHover();
    setHoverNavigationPage(null);
    onPageSelect?.(pageNumber);
    const dragPages = selectedPageSet.has(pageNumber) ? selectedPages : [pageNumber];
    if (!selectedPageSet.has(pageNumber)) {
      setSelectedPages(dragPages);
      setSelectionAnchor(pageNumber);
    }
    setDraggedPages(dragPages);
    setDragPreviewTargetPage(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', JSON.stringify(dragPages));
  }, [cancelPageHover, disabled, onPageSelect, selectedPageSet, selectedPages]);

  const handlePageDragOver = useCallback((event, pageNumber) => {
    if (disabled) return;
    event.preventDefault();
    cancelPageHover();
    setHoverNavigationPage(null);
    setDragPreviewTargetPage(pageNumber);
  }, [cancelPageHover, disabled]);

  const clearDragPreview = useCallback(() => {
    setDraggedPages([]);
    setDragPreviewTargetPage(null);
  }, []);

  const handlePageDrop = useCallback((event, pageNumber) => {
    if (disabled) return;
    event.preventDefault();
    let draggedPages = selectedPages;
    try {
      const parsedPages = JSON.parse(event.dataTransfer.getData('text/plain'));
      if (Array.isArray(parsedPages)) draggedPages = parsedPages;
    } catch (error) {
      // The selected page state is the fallback for browsers that omit drag data.
    }
    clearDragPreview();
    mergePagesIntoOneDocument(draggedPages, pageNumber);
  }, [clearDragPreview, disabled, mergePagesIntoOneDocument, selectedPages]);

  const updateDocumentType = useCallback((documentIndex, type) => {
    updateDocuments(documents.map((document, index) => (
      index === documentIndex ? { ...document, type } : document
    )));
  }, [documents, updateDocuments]);

  const toggleDocumentFilter = useCallback((document) => {
    const documentPage = Number(document?.pagenbs);
    if (!Number.isInteger(documentPage)) return;
    const nextDocumentPage = activeDocumentPage === documentPage ? null : documentPage;
    setActiveDocumentPage(nextDocumentPage);
    setSelectedPages([]);
    setSelectionAnchor(null);
    onDocumentFilterChange?.(nextDocumentPage == null ? null : document.pages);
  }, [activeDocumentPage, onDocumentFilterChange]);

  const clearDocumentFilter = useCallback(() => {
    setActiveDocumentPage(null);
    setSelectedPages([]);
    setSelectionAnchor(null);
    onDocumentFilterChange?.(null);
  }, [onDocumentFilterChange]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, lg: 1.5 },
        borderColor: 'warning.light',
        bgcolor: 'rgba(255, 251, 235, 0.72)',
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        {t('validation_workspace.split_review_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('validation_workspace.split_review_editor_help')}
      </Typography>

      <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mr: 'auto' }}>
          {t('validation_workspace.split_review_selected_pages', { count: selectedPages.length })}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || !visiblePageNumbers.length}
          onClick={() => setSelectedPages(visiblePageNumbers)}
        >
          {t('validation_workspace.split_review_select_all')}
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={disabled || !selectedPages.length}
          onClick={() => {
            setSelectedPages([]);
            setSelectionAnchor(null);
          }}
        >
          {t('validation_workspace.split_review_clear_selection')}
        </Button>
        {selectedPages.length > 0 && (
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => createDocumentFromSelectedPages(selectedPages)}
            sx={{ width: { xs: '100%', sm: 'auto' }, textTransform: 'none' }}
          >
            {t('validation_workspace.split_review_group_selected', { count: selectedPages.length })}
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t('validation_workspace.split_review_drag_hint')}
      </Typography>
      {activeDocumentIndex >= 0 && (
        <Button
          size="small"
          variant="text"
          onClick={clearDocumentFilter}
          sx={{ mt: 0.25, px: 0, minWidth: 0, textTransform: 'none' }}
        >
          {t('validation_workspace.split_review_show_all_documents')}
        </Button>
      )}
      <TextField
        select
        size="small"
        label={t('validation_workspace.split_review_page_view_label')}
        value={pageView}
        onChange={(event) => setPageView(event.target.value)}
        sx={{ mt: 0.75, minWidth: { xs: '100%', sm: 180 } }}
      >
        <MenuItem value="compact">
          {t('validation_workspace.split_review_page_view_compact')}
        </MenuItem>
        <MenuItem value="list">
          {t('validation_workspace.split_review_page_view_list')}
        </MenuItem>
        <MenuItem value="thumbnails">
          {t('validation_workspace.split_review_page_view_thumbnails')}
        </MenuItem>
      </TextField>
      <Box
        ref={thumbnailContainerRef}
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: pageViewConfig.containerColumns,
          gap: 0.5,
          maxHeight: { xs: 420, lg: 520 },
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {visiblePageNumbers.map((pageNumber) => {
          const documentIndex = getDocumentIndexForPage(documents, pageNumber);
          const documentName = documents[documentIndex]?.type || 'document';
          const previewDocumentIndex = dragPreviewTargetPage != null && draggedPageSet.has(pageNumber)
            ? getDocumentIndexForPage(documents, dragPreviewTargetPage)
            : documentIndex;
          const previewDocumentName = documents[previewDocumentIndex]?.type || documentName;
          const isDragPreview = previewDocumentName !== documentName;
          const displayedDocumentName = isDragPreview ? previewDocumentName : documentName;
          const documentColorSeed = getDocumentColorSeed(displayedDocumentName, previewDocumentIndex);
          const documentBackgroundColor = generatePastelColor(documentColorSeed);
          const documentAccentColor = generateSaturatedColor(documentColorSeed);
          const thumbnail = thumbnailByPage.get(pageNumber);
          const visionOrientation = visionOrientationByPage[pageNumber] || { rotation: 0, fineAngle: 0 };
          const isSelected = selectedPageSet.has(pageNumber);
          const isPageHovered = hoveredPage === pageNumber;
          const isHoverNavigationLoading = hoverNavigationPage === pageNumber;
          return (
            <Paper
              key={pageNumber}
              ref={(node) => setThumbnailCardRef(pageNumber, node)}
              data-page-number={pageNumber}
              component="button"
              type="button"
              variant="outlined"
              draggable={!disabled}
              onMouseEnter={() => {
                setHoveredPage(pageNumber);
                schedulePageHover(pageNumber);
              }}
              onMouseLeave={() => {
                cancelPageHover();
                setHoveredPage((previousPage) => (
                  previousPage === pageNumber ? null : previousPage
                ));
                setHoverNavigationPage((previousPage) => (
                  previousPage === pageNumber ? null : previousPage
                ));
              }}
              onDragStart={(event) => handlePageDragStart(event, pageNumber)}
              onDragOver={(event) => handlePageDragOver(event, pageNumber)}
              onDragEnd={clearDragPreview}
              onDrop={(event) => handlePageDrop(event, pageNumber)}
              onClick={(event) => togglePageSelection(pageNumber, event)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  togglePageSelection(pageNumber, event);
                }
              }}
              aria-pressed={isSelected}
              aria-label={t('validation_workspace.split_review_page_with_document', {
                page: pageNumber,
                document: documentName,
              })}
              aria-busy={isHoverNavigationLoading}
              sx={{
                minWidth: 0,
                width: 'auto',
                position: 'relative',
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: pageViewConfig.cardColumns,
                alignItems: 'center',
                gap: 0.9,
                p: 0.7,
                textAlign: 'left',
                 cursor: disabled ? 'default' : 'pointer',
                 borderColor: isPageHovered || isSelected ? 'primary.main' : 'divider',
                 borderWidth: isSelected ? 2 : 1,
                 bgcolor: isPageHovered || isSelected ? 'rgba(87, 87, 176, 0.1)' : 'background.paper',
                 boxShadow: isPageHovered || isSelected ? '0 0 0 1px rgba(87, 87, 176, 0.18)' : 'none',
                 transform: isPageHovered ? 'translateY(-1px)' : 'none',
                 transition: 'transform 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                 '&::after': {
                   content: '""',
                   position: 'absolute',
                   left: 0,
                   right: 0,
                   bottom: 0,
                   height: isHoverNavigationLoading ? 3 : 0,
                   background: 'linear-gradient(90deg, #5757b0, #8c8cda)',
                   transformOrigin: 'left center',
                   transform: isHoverNavigationLoading ? 'scaleX(1)' : 'scaleX(0)',
                   transition: isHoverNavigationLoading
                     ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1), height 120ms ease'
                     : 'transform 120ms ease, height 120ms ease',
                 },
                 '&:active': { cursor: disabled ? 'default' : 'grabbing' },
               }}
             >
               <Box
                sx={{
                  width: pageViewConfig.thumbnailWidth,
                  height: pageViewConfig.thumbnailHeight,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  borderRadius: 0.75,
                  overflow: 'hidden',
                }}
              >
                {thumbnail ? (
                  <Box
                    component="img"
                    src={thumbnail}
                    alt={t('validation_workspace.split_review_page_alt', { page: pageNumber })}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transform: Math.abs(visionOrientation.fineAngle) >= 0.001
                        ? `rotate(${-visionOrientation.fineAngle}deg)`
                        : 'none',
                      transformOrigin: 'center center',
                    }}
                  />
                ) : thumbnailLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                    {pageNumber}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.45,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: '0.66rem', lineHeight: 1.1 }}
                >
                  {t('validation_workspace.split_review_page_label', { page: pageNumber })}
                </Typography>
                <Chip
                  size="small"
                  label={displayedDocumentName}
                  title={displayedDocumentName}
                  sx={{
                    height: pageViewConfig.documentChipHeight,
                    maxWidth: '100%',
                    alignSelf: 'flex-start',
                    bgcolor: documentBackgroundColor,
                    color: documentAccentColor,
                    border: `1px solid ${documentAccentColor}`,
                    fontWeight: 700,
                    ...(isDragPreview && {
                      boxShadow: '0 0 0 1px rgba(87, 87, 176, 0.18)',
                    }),
                    '& .MuiChip-label': {
                      px: 0.65,
                      fontSize: pageViewConfig.documentFontSize,
                      lineHeight: 1.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              </Box>
            </Paper>
          );
        })}
      </Box>

      {thumbnailError && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {thumbnailError}
        </Alert>
      )}

      <Divider sx={{ my: 0.75 }} />
      <Typography variant="caption" fontWeight="bold">
        {t('validation_workspace.split_review_summary_title')}
      </Typography>
      <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {documents.map((document, index) => {
          const documentColorSeed = getDocumentColorSeed(document.type, index);
          const documentBackgroundColor = generatePastelColor(documentColorSeed);
          const documentAccentColor = generateSaturatedColor(documentColorSeed);
          const isActiveDocument = activeDocumentIndex === index;
          return (
            <Box
              key={`${document.pagenbs}-${index}`}
              role="button"
              tabIndex={0}
              aria-pressed={isActiveDocument}
              onClick={() => toggleDocumentFilter(document)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleDocumentFilter(document);
                }
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) 150px' },
                alignItems: 'center',
                gap: 0.5,
                minHeight: 36,
                py: 0.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                cursor: 'pointer',
                bgcolor: isActiveDocument ? 'rgba(87, 87, 176, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(87, 87, 176, 0.06)' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                },
              }}
            >
              <Chip
                size="small"
                label={document.type}
                title={document.type}
                sx={{
                  bgcolor: documentBackgroundColor,
                  color: documentAccentColor,
                  border: `1px solid ${documentAccentColor}`,
                  fontWeight: 700,
                  justifySelf: 'start',
                  maxWidth: '100%',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  overflowWrap: 'anywhere',
                }}
              >
                {t('validation_workspace.split_review_pages_list', { pages: formatPageRanges(document.pages) })}
              </Typography>
              <TextField
                size="small"
                variant="standard"
                value={document.type}
                onChange={(event) => updateDocumentType(index, event.target.value)}
                disabled={disabled}
                label={t('validation_workspace.split_review_document_name')}
                fullWidth
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{
                  minWidth: 0,
                  gridColumn: { xs: '1 / -1', sm: 'auto' },
                  '& .MuiInputBase-root': { fontSize: '0.8rem' },
                }}
              />
            </Box>
          );
        })}
      </Box>

      {documents.length < 2 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('validation_workspace.split_review_min_documents')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={disabled || saving || documents.length < 2}
          onClick={onApprove}
        >
          {t('validation_workspace.split_review_approve')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          disabled={disabled || saving}
          onClick={onReject}
        >
          {t('validation_workspace.split_review_reject')}
        </Button>
      </Box>
    </Paper>
  );
};

export default SplitReviewEditor;
